// Casa de marcat fiscale (Roumanie) — pilote FiscalNet d'EconMedia.
//
// UN SEUL transport, celui que documente le constructeur (« Utilizare Driver
// FiscalNet », EconMedia, §5) :
//   écrire les commandes dans  ...\Bonuri\<id>.txt
//   lire le résultat dans      ...\Raspuns\<id>.txt
//
// Le pilote surveille \Bonuri et imprime dès qu'un fichier y apparaît. Il n'y a
// pas d'API réseau documentée : FiscalNet Pro 2.7.9 expose bien un serveur web
// sur le port 65400, mais aucune spécification ne le couvre — on ne bâtit pas
// l'encaissement sur une interface non spécifiée qui peut changer à la
// prochaine mise à jour du pilote.
//
// Conséquence d'architecture : un navigateur ne peut pas écrire dans \Bonuri.
// Ce module ne fait donc QUE produire le contenu exact du fichier de commandes
// et lire une réponse. Le dépôt du fichier est le travail de l'agent qui tourne
// sur la machine du pilote (XYLO-X/CasaMarcat).
//
// Matériel en service : Datecs DP-25 MX, 192.168.1.52, pilote sur 192.168.1.249.
// La caisse doit être en mode 6 « Conexiune PC » sinon le pilote renvoie -33022.

// Clé localStorage : la config est par poste, pas par utilisateur — les chemins
// \Bonuri/\Raspuns dépendent de l'installation du pilote, pas du compte connecté.
export const STORAGE_KEY = 'xylo:fiscalnet-config';

// Chemins par défaut de l'installation FiscalNet.
export const DEFAULT_BONURI = 'C:\\FiscalNet\\Bonuri';
export const DEFAULT_RASPUNS = 'C:\\FiscalNet\\Raspuns';

// P^TipPlata^VALOARE — codes imposés par le pilote.
export const PAYMENT_TYPES = [
  { code: 1, label: 'Numerar (espèces)' },
  { code: 2, label: 'Card' },
  { code: 3, label: 'Credit' },
  { code: 4, label: 'Tichet masă' },
  { code: 5, label: 'Tichet valoric' },
  { code: 6, label: 'Voucher' },
  { code: 7, label: 'Plată modernă' },
  { code: 8, label: 'Alte modalități' },
];

// CB^CodDeBare^TipCod
export const BARCODE_TYPES = [
  { code: 1, label: 'EAN8' },
  { code: 2, label: 'EAN13' },
  { code: 3, label: 'Code128' },
  { code: 4, label: 'QR Code' },
];

/**
 * Entier « sans délimiteur » attendu par le pilote : 10 RON → "1000",
 * 1 pièce → "1000" (3 décimales pour les quantités).
 *
 * On passe par toFixed() plutôt que Math.round(n * 100) : ce dernier casse sur
 * les binaires non représentables et donnerait un centime de moins sur des
 * montants parfaitement ordinaires.
 *
 * @param {number|string} value montant en RON, ou quantité
 * @param {number} decimals 2 pour les prix, 3 pour les quantités
 * @returns {string|null} null si la valeur n'est pas un nombre fini
 */
export function fmtAmount(value, decimals = 2) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  const sign = n < 0 ? '-' : '';
  // Les zéros de tête doivent partir : 0,50 RON s'écrit « 50 », pas « 050 »
  // (cf. MV^50 dans les bons d'exemple du pilote). Le lookahead garde le
  // dernier chiffre, sinon un montant nul deviendrait la chaîne vide.
  const digits = Math.abs(n).toFixed(decimals).replace('.', '').replace(/^0+(?=\d)/, '');
  return sign + digits;
}

export const fmtPrice = (ron) => fmtAmount(ron, 2);
export const fmtQty = (qty) => fmtAmount(qty, 3);

/**
 * `^` est le séparateur de champs et les retours à la ligne séparent les
 * commandes : un libellé produit qui en contient décale toute la ligne.
 * Les libellés XYLO viennent de la base (noms de produits, descriptions
 * libres saisies sur le devis), donc ça arrive pour de vrai.
 */
export function sanitizeText(value) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/[\r\n]+/g, ' ').replace(/\^/g, '-').trim();
}

/**
 * Ligne de vente : S^DENUMIRE^PRET^CANTITATE^UM^GRTVA^GRDEP
 *
 * ATTENTION à l'ordre des champs. La doc publiée sur driverfiscal.ro annonce
 * « S^LIBELLÉ^PRIXU^PRIXV^UNITÉ^CANTITÉ^TVA », ce qui est un mauvais découpage :
 * la doc EconMedia du pilote et les bons d'exemple livrés avec (BonuriTest)
 * donnent bien prix PUIS quantité PUIS unité. Se tromper ici inverse prix et
 * quantité sans erreur visible — le bon sort, avec le mauvais montant.
 *
 * `grtva` est un NUMÉRO DE GROUPE (1..5), pas un pourcentage : la
 * correspondance groupe → taux est programmée dans la caisse elle-même.
 */
export function saleLine({ name, price, qty = 1, um = 'buc', grtva = 1, grdep = 1 }) {
  return ['S', sanitizeText(name), fmtPrice(price), fmtQty(qty), um || 'buc', grtva, grdep].join('^');
}

/** Ligne de storno (retour/annulation d'article) : mêmes champs que S^. */
export function voidLine({ name, price, qty = 1, um = 'buc', grtva = 1, grdep = 1 }) {
  return ['VS', sanitizeText(name), fmtPrice(price), fmtQty(qty), um || 'buc', grtva, grdep].join('^');
}

export const discountPercentLine = (pct) => `DP^${fmtPrice(pct)}`;
export const discountValueLine = (ron) => `DV^${fmtPrice(ron)}`;
export const surchargePercentLine = (pct) => `MP^${fmtPrice(pct)}`;
export const surchargeValueLine = (ron) => `MV^${fmtPrice(ron)}`;
export const subtotalLine = () => 'ST^';
export const freeTextLine = (text) => `TL^${sanitizeText(text)}`;
export const fiscalCodeLine = (cif) => `CF^${sanitizeText(cif)}`;
export const paymentLine = (type, ron) => `P^${type}^${fmtPrice(ron)}`;
export const barcodeLine = (value, type = 2) => `CB^${sanitizeText(value)}^${type}`;

// Commandes de service, sans effet fiscal.
export const reportXLine = () => 'X^';
export const openDrawerLine = () => 'DS^';

// Mouvements de tiroir : I^ = introducere (entrée), O^ = scoatere (sortie).
export const cashInLine = (ron) => `I^${fmtPrice(ron)}`;
export const cashOutLine = (ron) => `O^${fmtPrice(ron)}`;

/**
 * Message sur l'afficheur client : MD^Rand1^Rand2.
 * Ne s'exécute QUE si le fichier déposé est nommé « display_… » — cf.
 * receiptFileName(). Déposé sans ce préfixe, il partirait en bon fiscal.
 */
export const displayLine = (l1 = '', l2 = '') => `MD^${sanitizeText(l1)}^${sanitizeText(l2)}`;

/**
 * Envoi d'un montant vers le terminal bancaire : POS^VALOARE.
 *
 * Réservé à FiscalNet **Plus** (l'installation en service est une Pro : cette
 * commande y sera refusée). Et c'est la seule commande dont la doc annonce
 * explicitement un BONOK=-1 : somme partie vers le terminal, réponse non
 * identifiée. Ne jamais rejouer automatiquement — double encaissement.
 */
export const posLine = (ron) => `POS^${fmtPrice(ron)}`;

/**
 * Copie du dernier bon : COPIEBON^.
 * Présente dans les bons d'exemple livrés avec le pilote, mais ABSENTE de la
 * doc écrite — donc non garantie d'une version à l'autre.
 */
export const receiptCopyLine = () => 'COPIEBON^';

// Commandes à effet irréversible — isolées ici pour qu'aucun appel ne
// puisse les produire par accident depuis un builder générique.
export const reportZLine = () => 'Z^';
export const cancelReceiptLine = () => 'VB^';

/**
 * Construit les lignes d'un bon complet.
 *
 * L'ordre imposé par le pilote n'est pas négociable :
 *   1. CF^ (code fiscal client) en tête si présent
 *   2. pour chaque article : S^ puis IMMÉDIATEMENT sa remise/majoration
 *   3. ST^ si l'on veut appliquer une remise globale sur le sous-total
 *   4. TL^ (texte libre) APRÈS les articles, AVANT les paiements
 *   5. P^ (paiements) en dernier
 * Une remise placée ailleurs qu'immédiatement sous son article s'applique
 * silencieusement à l'article précédent.
 *
 * @param {object} o
 * @param {string} [o.cif] code fiscal du client (CF^)
 * @param {Array}  o.items { name, price, qty, um, grtva, grdep,
 *                           discountPercent, discountValue,
 *                           surchargePercent, surchargeValue, storno }
 * @param {number} [o.subtotalDiscountPercent] remise globale via ST^
 * @param {string[]} [o.freeText] lignes TL^
 * @param {object} [o.barcode] { value, type }
 * @param {Array}  o.payments { type, amount }
 * @returns {{ lines: string[], errors: string[] }}
 */
export function buildReceipt({
  cif = '',
  items = [],
  subtotalDiscountPercent = null,
  freeText = [],
  barcode = null,
  payments = [],
} = {}) {
  const lines = [];
  const errors = [];

  if (cif && String(cif).trim() !== '') lines.push(fiscalCodeLine(cif));

  if (!items.length) errors.push('Aucun article : un bon fiscal doit contenir au moins une ligne de vente.');

  items.forEach((item, i) => {
    const label = `Article ${i + 1}${item.name ? ` (${item.name})` : ''}`;

    if (!item.name || String(item.name).trim() === '') errors.push(`${label} : libellé vide.`);

    // Le pilote refuse les montants négatifs sur S^ : un retour se code en VS^,
    // pas en prix négatif (c'est ce que teste bon_eroare.txt livré avec le pilote).
    if (Number(item.price) < 0 || Number(item.qty) < 0) {
      errors.push(`${label} : prix ou quantité négatifs — utiliser un storno (VS^) plutôt qu'un montant négatif.`);
    }
    if (fmtPrice(item.price) === null) errors.push(`${label} : prix invalide.`);
    if (fmtQty(item.qty === undefined ? 1 : item.qty) === null) errors.push(`${label} : quantité invalide.`);

    const grtva = Number(item.grtva);
    if (!Number.isInteger(grtva) || grtva < 1 || grtva > 5) {
      errors.push(`${label} : groupe de TVA « ${item.grtva} » hors plage — le pilote n'accepte que 1 à 5.`);
    }

    lines.push(item.storno ? voidLine(item) : saleLine(item));

    // Une seule modification par article : le pilote applique la commande qui
    // suit la ligne d'article, en empiler plusieurs donne un résultat non défini.
    const mods = [
      item.discountPercent != null && discountPercentLine(item.discountPercent),
      item.discountValue != null && discountValueLine(item.discountValue),
      item.surchargePercent != null && surchargePercentLine(item.surchargePercent),
      item.surchargeValue != null && surchargeValueLine(item.surchargeValue),
    ].filter(Boolean);

    if (mods.length > 1) {
      errors.push(`${label} : ${mods.length} remises/majorations sur la même ligne — une seule est appliquée.`);
    }
    if (mods.length) lines.push(mods[0]);
  });

  if (subtotalDiscountPercent != null) {
    lines.push(subtotalLine());
    lines.push(discountPercentLine(subtotalDiscountPercent));
  }

  if (barcode && barcode.value) lines.push(barcodeLine(barcode.value, barcode.type || 2));

  (freeText || []).filter((t) => t && String(t).trim() !== '').forEach((t) => lines.push(freeTextLine(t)));

  if (!payments.length) errors.push('Aucun paiement : le bon resterait ouvert sur la caisse.');
  payments.forEach((p, i) => {
    if (fmtPrice(p.amount) === null) errors.push(`Paiement ${i + 1} : montant invalide.`);
    lines.push(paymentLine(p.type, p.amount));
  });

  return { lines, errors };
}

/** Total attendu, pour comparer au bon imprimé. Remises incluses. */
export function receiptTotal(items = []) {
  return items.reduce((sum, it) => {
    if (it.storno) return sum;
    let line = Number(it.price || 0) * Number(it.qty === undefined ? 1 : it.qty);
    if (it.discountPercent != null) line -= (line * Number(it.discountPercent)) / 100;
    if (it.discountValue != null) line -= Number(it.discountValue);
    if (it.surchargePercent != null) line += (line * Number(it.surchargePercent)) / 100;
    if (it.surchargeValue != null) line += Number(it.surchargeValue);
    return sum + line;
  }, 0);
}

/** Contenu exact d'un fichier ...\Bonuri\<id>.txt (transport fichier). */
export function receiptToText(lines) {
  return (lines || []).join('\r\n');
}

/**
 * Nom du fichier à déposer dans \Bonuri.
 *
 * Deux règles de la doc qu'on ne peut pas deviner et qui n'ont pas d'équivalent
 * ailleurs dans le protocole — c'est le NOM DU FICHIER, pas son contenu, qui
 * décide du type de sortie :
 *   • bon non fiscal   → le nom doit commencer par « nf_ »
 *   • message afficheur → le nom doit commencer par « display_ »
 * Un bon non fiscal déposé sans son préfixe part en bon FISCAL : il entre dans
 * le journal électronique et ne peut plus être retiré.
 *
 * Et le nommage par identifiant d'opération n'est pas une préférence : la doc
 * le rend obligatoire dès que plusieurs postes impriment sur la même caisse,
 * sinon deux bons concurrents s'écrasent et leurs réponses se mélangent.
 *
 * @param {object} o
 * @param {string|number} o.id identifiant de l'opération XYLO
 * @param {'fiscal'|'nonfiscal'|'display'} [o.kind]
 * @returns {string}
 */
export function receiptFileName({ id, kind = 'fiscal' } = {}) {
  const safe = String(id === undefined || id === null ? '' : id)
    .trim()
    .replace(/[^A-Za-z0-9_-]/g, '_');

  if (safe === '') throw new Error('receiptFileName : identifiant d’opération vide.');

  const prefix = kind === 'nonfiscal' ? 'nf_' : kind === 'display' ? 'display_' : '';
  return `${prefix}${safe}.txt`;
}

/**
 * Lit le contenu d'un fichier ...\Raspuns\<id>.txt.
 *
 * Format documenté (EconMedia §« Evaluarea corectitudinii executarii comenzii »),
 * 2 lignes ou plus :
 *   BONOK=1 | BONOK=0        1 = bon émis correctement, 0 = incorrect
 *   NRBON=?                  numéro du bon si émis correctement
 *   ERRCODE=? / ERRINFO=?    code et libellé d'erreur sinon
 *
 * `ok` vaut trois choses, et la distinction est le cœur de cette fonction :
 *   true  → bon émis
 *   false → échec franc, on peut rejouer
 *   null  → INDÉTERMINÉ, ne JAMAIS rejouer automatiquement
 *
 * Le cas null n'est pas défensif, il est documenté : sur `POS^`, BONOK=-1
 * signifie que la somme est partie vers le terminal bancaire mais que la
 * réponse n'a pas pu être identifiée. Un rejeu encaisserait deux fois. Un
 * fichier réponse absent ou illisible tombe dans le même seau : sans réponse,
 * on ne sait pas si le bon est sorti.
 *
 * @param {string} raw contenu brut du fichier réponse
 * @returns {{ ok:(boolean|null), receiptNo:?string, errCode:?string, errInfo:?string }}
 */
export function interpretResponse(raw) {
  const empty = { ok: null, receiptNo: null, errCode: null, errInfo: null };
  if (typeof raw !== 'string' || raw.trim() === '') return empty;

  const kv = {};
  raw.split(/[\r\n]+/).forEach((line) => {
    const m = line.match(/^\s*([A-Za-z]+)\s*=\s*(.*?)\s*$/);
    if (m) kv[m[1].toUpperCase()] = m[2];
  });

  if (!('BONOK' in kv)) return empty;

  const flag = kv.BONOK.trim();
  // -1 avant tout le reste : c'est le seul cas où un « échec » apparent doit
  // être remonté comme indéterminé plutôt que comme false.
  const ok = flag === '-1' ? null : flag === '1';

  const orNull = (v) => (v === undefined || v.trim() === '' ? null : v.trim());

  return {
    ok,
    receiptNo: orNull(kv.NRBON),
    errCode: orNull(kv.ERRCODE),
    errInfo: orNull(kv.ERRINFO),
  };
}

const CONFIG_DEFAULTS = {
  bonuri: DEFAULT_BONURI,
  raspuns: DEFAULT_RASPUNS,
  grtva: 1,
  grdep: 1,
};

export function loadConfig() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...CONFIG_DEFAULTS };
    return { ...CONFIG_DEFAULTS, ...JSON.parse(raw) };
  } catch (e) {
    return { ...CONFIG_DEFAULTS };
  }
}

export function saveConfig(cfg) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cfg));
    return true;
  } catch (e) {
    return false;
  }
}
